import GxepActions from "data/GxepActions";
import SearchFormActions from "data/SearchFormActions";
import SearchFormStore from "data/SearchFormStore";
import { ACTION_CONTEXT_KEY } from "data/util/ContextedReduceStore";
import loadObjectOf from "data/util/loadObjectOf";
import "data/util/typeCheckingStore";
import typeCheckingStore from "data/util/typeCheckingStore";
import Dispatcher from "Dispatcher";
import dotProp from "dot-prop-immutable";
import { ReduceStore } from "flux/utils";
import PropTypes from "prop-types";
import DataManager from "./DataManager";
import LoadObject from "./LoadObject";
import {
    fromSlice,
    newSlice,
    sliceTypeBuilder,
    toSlice,
} from "./slices";
import { hotLoadObject } from "./storeUtils";
import UserActions from "./UserActions";
import EntityName from "data/util/EntityName";

const IMPLEMENTATION_ADT = "adt";

const IMPLEMENTATION_HEMO = "hemo";

export const PERMISSIONS = {
    READ: "READ",
    WRITE: "WRITE",
    CREATE: "CREATE",
    DELETE: "DELETE",
    ACCESS: "ACCESS",
    EXPORT: "EXPORT",
    MERGE: "MERGE",
    PREFERENCE_MANAGER: "PREFERENCE_MANAGER",
    HARVEST: "HARVEST",
    LOOKUP_MANAGER: "LOOKUP_MANAGER",
    AI_SUGGESTION: "AI_SUGGESTION"
};

const PERMISSION_INDEXES = [
    PERMISSIONS.READ,
    PERMISSIONS.WRITE,
    PERMISSIONS.CREATE,
    PERMISSIONS.DELETE,
    null, // ADMINISTRATION
    null, // READ_DEIDENTIFIED
    PERMISSIONS.ACCESS,
    PERMISSIONS.EXPORT,
    PERMISSIONS.MERGE,
    PERMISSIONS.PREFERENCE_MANAGER,
    PERMISSIONS.HARVEST,
    PERMISSIONS.LOOKUP_MANAGER,
    PERMISSIONS.AI_SUGGESTION
];

export const I_PERMISSIONS = Object.keys(PERMISSIONS)
    .reduce(
        (agg, k) => {
            agg[k] = 1 << PERMISSION_INDEXES.indexOf(PERMISSIONS[k]);
            return agg;
        },
        {}
    );

const GENERIC_XML_EXPORT_PASSWORD_PREF = "generic-xml-export-encryption-password";

export const withUpdatedLicensePreference = (state, impl, pref, value) => dotProp.set(
    state,
    [
        "orgs",
        "byKey",
        state.activeOrg,
        "lics",
        "byKey",
        impl,
        "prefs",
        pref,
    ],
    value,
);

export const withUpdatedOrgPreference = (state, pref, value) => dotProp.set(
    state,
    [
        "orgs",
        "byKey",
        state.activeOrg,
        "prefs",
        pref,
    ],
    value,
);

export const getFirstName = raw =>
    raw.firstname;

export const getLastName = raw =>
    raw.lastname;

export const getUsername = raw =>
    raw.username;

export const getDisplayName = raw =>
    raw.firstname || raw.username;

export const getActiveOrgKey = raw =>
    raw.activeOrg;

const _getOrg = (raw, orgKey) => {
    if (orgKey == null) {
        orgKey = raw.activeOrg;
    }
    if (!raw.orgs.byKey.hasOwnProperty(orgKey)) {
        throw new Error(`No '${orgKey}' organization is known`);
    }
    return raw.orgs.byKey[orgKey];
};

// don't allow an unintended orgKey to slip through
export const getActiveOrg = raw => _getOrg(raw);

export const getAllOrgs = raw => fromSlice(raw.orgs, o =>
    Object.assign({active: o.key === raw.activeOrg}, o));

export const getNavTree = raw => raw.navTree;

export const getImplsWithValidationAdmin = raw =>
    _getImplsWithAdminAvailable(raw, "validation-show-admin", "validation-excluded-fields");

export const getImplsWithHiddenFieldAdmin = raw => canUseDE(raw)
    ? _getImplsWithAdminAvailable(raw, "user-hidden-field-admin", "user-hidden-fields")
    : [];

const _getImplsWithAdminAvailable = (raw, flag, pref) => fromSlice(_getOrg(raw).lics)
    .filter(l => {
        const show = l.prefs[flag];
        const isAdmin = allGrants(l.igrants, I_PERMISSIONS.PREFERENCE_MANAGER);
        const isUnlocked = l.prefLocks[pref] !== "NONE";
        return show && isAdmin && isUnlocked;
    });

export const isPrefManForAnything = raw =>
    !!fromSlice(raw.orgs).find(o =>
        !!fromSlice(o.lics).find(l =>
            allGrants(l.igrants, I_PERMISSIONS.PREFERENCE_MANAGER)));

const _getRawLicense = (raw, implKey, orgKey = null) => {
    const org = _getOrg(raw, orgKey);
    if (!org.lics.byKey.hasOwnProperty(implKey)) {
        throw new Error(`No '${implKey}' license is known for '${org.key}'`);
    }
    return org.lics.byKey[implKey];
};

const _hasFeature = (holder, feature) =>
    holder.features && holder.features.has(feature);

export const hasLicenseFeature = (raw, feature, impl) =>
    _hasFeature(_getRawLicense(raw, impl), feature);

const _getPref = (holder, pref) => {
    const prefs = holder.prefs;
    if (!prefs.hasOwnProperty(pref)) {
        throw new Error(`No '${pref}' preference is known`);
    }
    return prefs[pref];
};

export const getLicensePreference = (raw, pref, impl, org = null) =>
    _getPref(_getRawLicense(raw, impl, org), pref);

export const getOrganizationalPreference = (raw, pref, org = null) =>
    _getPref(_getOrg(raw, org), pref);

export const isGenericXmlExportAvailable = (raw, org = null) =>
    !!getOrganizationalPreference(raw, GENERIC_XML_EXPORT_PASSWORD_PREF, org);

export const getLicensePreferenceMutability = (raw, pref, impl, org = null) => {
    if (org == null) {
        org = raw.activeOrg;
    }
    const locks = _getRawLicense(raw, impl, org).prefLocks;
    if (!locks.hasOwnProperty(pref)) {
        throw new Error(`No '${pref}' preference is known`);
    }
    return locks[pref];
};

/** DEPRECATED: use anyGrant or allGrants instead */
export const checkGrant = (igrants, perm) => {
    if (typeof perm === "string") {
        if (!I_PERMISSIONS.hasOwnProperty(perm)) {
            throw new Error(`No '${perm}' permission is known`);
        }
        perm = I_PERMISSIONS[perm];
    }
    return anyGrant(igrants, perm);
};

export const anyGrant = (igrants, perm) =>
    (igrants & perm) !== 0;

export const allGrants = (igrants, perm) =>
    (igrants & perm) === perm;

export const canUseDE = raw =>
    !raw.aggregate && fromSlice(_getOrg(raw).lics)
        .some(l => allGrants(l.igrants, I_PERMISSIONS.READ));

export const canUseDqr = raw => fromSlice(_getOrg(raw).lics)
    .some(l => allGrants(l.igrants, I_PERMISSIONS.READ));

export const canUseHex = raw =>
    fromSlice(_getOrg(raw).lics)
        .some(l => l.exporters != null);

const buildPermCheck = perm => (raw, implKey) =>
    allGrants(_getRawLicense(raw, implKey).igrants, perm);

/**
 * By fiat, "every license" ignores the ADT and Hemo implementations, as
 * they are data-less.
 */
const checkGrantAcrossEveryLicense = (raw, grant) =>
    fromSlice(_getOrg(raw).lics)
        .every(l => l.key === IMPLEMENTATION_ADT
            || l.key === IMPLEMENTATION_HEMO
            || allGrants(l.igrants, grant));

export const canWrite = buildPermCheck(I_PERMISSIONS.WRITE);

export const canDelete = buildPermCheck(I_PERMISSIONS.DELETE);

export const canCreate = buildPermCheck(I_PERMISSIONS.CREATE);

export const canPrint = buildPermCheck(I_PERMISSIONS.EXPORT);

export const canManageLookups = buildPermCheck(I_PERMISSIONS.LOOKUP_MANAGER);

export const canAISuggestion = buildPermCheck(I_PERMISSIONS.AI_SUGGESTION);

const buildMergePermCheck = perm => raw => {
    const org = getActiveOrg(raw);
    if (!org.completeAccess) return false;
    return checkGrantAcrossEveryLicense(raw, perm);
};

// and any other normal record, I believe?
export const canMergeMagicRoots = buildMergePermCheck(I_PERMISSIONS.MERGE | I_PERMISSIONS.DELETE);

export const canMergeLookups = buildMergePermCheck(I_PERMISSIONS.MERGE | I_PERMISSIONS.DELETE | I_PERMISSIONS.LOOKUP_MANAGER);

export const getHexForms = raw => {
    const all = fromSlice(_getOrg(raw).lics)
        .map(l => l.exporters)
        .filter(it => it != null)
        .reduce((a, v) => a.concat(v), []);
    // kludge-y deduplicate
    return fromSlice(toSlice(all, "key"));
};

const saveOrgUserPref = (state, pref, value) => {
    const curr = getOrganizationalPreference(state.getRequiredValue(), pref);
    if (curr === value) return state;
    return state.loading(DataManager.updateOrgUserPreference(pref, value));
};

function getDescendantOrgKeys(raw) {
    const org = getActiveOrg(raw);
    return [...new Set(fromSlice(org.lics)
        .flatMap(l => l.descendants))];
}

function getImplementationDescendantOrgKeys(raw, implKeys) {
    if (!implKeys || implKeys.length === 0) {
        throw new Error("You've requested descendants for zero implementations?!");
    }
    const org = getActiveOrg(raw);
    return [...new Set(implKeys
        .filter(k => org.lics.byKey.hasOwnProperty(k))
        .flatMap(k => org.lics.byKey[k].descendants))];
}

function getExporterFilterOrgKeys(raw, exporterKey) {
    const org = getActiveOrg(raw);
    const lics = fromSlice(org.lics)
        .filter(l => l.exporters.some(e => e.key === exporterKey));
    if (lics.length === 0) return [];
    const exp = lics[0].exporters.find(e => e.key === exporterKey);
    if (exp.aggregateSupport !== "FILTER") return []; // doesn't support filtering
    return [...new Set(lics
        .flatMap(l => l.descendants))];
}

export class UserStore extends ReduceStore {

    getInitialState() {
        return LoadObject.empty();
    }

    reduce(state, action) {
        switch (action.type) {
            case UserActions.START_LOAD: {
                return state.loading(
                    DataManager.loadUser(),
                );
            }

            case UserActions.LOADED: {
                const u = action.user;
                const activeOrgs = u.organizations.filter(it => it.active);
                if (activeOrgs.length !== 1) {
                    // per HO-143 - should eventually be a load error
                    throw new Error(`User must have exactly one active organization, not ${activeOrgs.length}.`);
                }
                const activeOrg = activeOrgs[0];
                if (process.env.NODE_ENV !== "production") {
                    if (!u.navTree || !u.navTree.items || u.navTree.items.length === 0) {
                        u.navTree = {
                            items: [{
                                label: "Hybrid Outcomes",
                                url: ".",
                            }, {
                                items: [{
                                    items: [],
                                    label: "Images",
                                    url: "https://www.google.com/imghp",
                                }, {
                                    items: [],
                                    label: "GMail",
                                    url: "https://mail.google.com/mail",
                                }],
                                label: "Google",
                                url: "https://www.google.com/",
                            }],
                        };
                    }
                }
                return state.receiveValue({
                    username: u.username,
                    firstname: u.firstname,
                    lastname: u.lastname,
                    superuser: !!u.superuser,
                    mode: u.mode,
                    navTree: u.navTree,
                    activeOrg: activeOrg.key,
                    aggregate: !!activeOrg.aggregate,
                    orgs: toSlice(u.organizations, "key", o => ({
                        name: o.name,
                        key: o.key,
                        completeAccess: o.completeAccess,
                        lics: toSlice(o.implementations, "key", l => ({
                            key: l.key,
                            name: l.name,
                            exporters: l.exporters != null && l.exporters.length > 0 ? l.exporters : null,
                            igrants: l.igrants,
                            prefs: Object.keys(l.preferences)
                                .reduce((m, p) => ({
                                    ...m,
                                    [p]: l.preferences[p][0]
                                }), {}),
                            prefLocks: Object.keys(l.preferences)
                                .reduce((m, p) => ({
                                    ...m,
                                    [p]: l.preferences[p][1]
                                }), {}),
                            features: new Set(l.features),
                            descendants: l.descendants,
                        })) || newSlice(),
                        prefs: { ...o.preferences },
                    })),
                });
            }

            case UserActions.LOAD_ERROR: {
                return state.receiveError(action.error);
            }

            case UserActions.UPDATE_LICENSE_PREFERENCE: {
                return state.loading(
                    DataManager.updateLicensePreference(
                        action.impl,
                        action.pref,
                        action.value,
                    ),
                );
            }

            case UserActions.LICENSE_PREFERENCE_UPDATED: {
                return state
                    .map(raw =>
                        withUpdatedLicensePreference(
                            raw,
                            action.impl,
                            action.pref,
                            action.newValue,
                        ),
                    ).done();
            }

            case UserActions.LICENSE_PREFERENCE_UPDATE_ERROR: {
                return state.setError(action.error).done();
            }

            case SearchFormActions.INITIATE_SEARCH_AND_SAVE_DEFAULT: {
                return saveOrgUserPref(
                    state,
                    "default-search-query",
                    JSON.stringify(SearchFormStore.getSearchToSave(action[ACTION_CONTEXT_KEY])));
            }

            case UserActions.UPDATE_ORG_USER_PREFERENCE: {
                return saveOrgUserPref(state, action.pref, action.value);
            }

            case UserActions.ORG_USER_PREFERENCE_UPDATED: {
                return state
                    .map(raw => {
                        raw = withUpdatedOrgPreference(
                            raw,
                            action.pref,
                            action.newValue,
                        );
                        // The server is the canonical home of this dependency,
                        // but the way the API works, there isn't a way to
                        // represent it across the wire. So double-implement for
                        // now. If it comes up again, we can revisit the API's
                        // structure to address this case more gracefully and
                        // then retrofit.
                        if (action.pref === "use-default-search" && !action.newValue) {
                            raw = withUpdatedOrgPreference(
                                raw,
                                "default-search-query",
                                "{}");
                        }
                        return raw;
                    }).done();
            }

            case UserActions.ORG_USER_PREFERENCE_UPDATE_ERROR: {
                return state.setError(action.error).done();
            }

            case GxepActions.SAVED: {
                // update the active org's password...
                return state.map(raw => {
                    const org = action.packet.find(o => o.key === raw.activeOrg);
                    if (org == null) return raw;
                    return withUpdatedOrgPreference(
                        raw,
                        GENERIC_XML_EXPORT_PASSWORD_PREF,
                        org.password,
                    );
                });
            }

            default:
                return state;
        }
    }

    getUserLO() {
        return hotLoadObject(
            () => this.getState(),
            () => Dispatcher.dispatch({
                type: UserActions.START_LOAD,
            }),
        );
    }

    isRealSession() {
        // this one shouldn't initiate
        const lo = this.getState();
        return ! lo.hasValue() || lo.getRequiredValue().mode !== "client";
    }

    getRawData() {
        return this.getState().getRequiredValue();
    }

    getFirstName() {
        return getFirstName(this.getRawData());
    }

    getLastName() {
        return getLastName(this.getRawData());
    }

    getUsername() {
        return getUsername(this.getRawData());
    }

    getDisplayName() {
        return getDisplayName(this.getRawData());
    }

    getActiveOrgKey() {
        return getActiveOrgKey(this.getRawData());
    }

    getActiveOrg() {
        return getActiveOrg(this.getRawData());
    }

    getAllOrgs() {
        return getAllOrgs(this.getRawData());
    }

    getNavTree() {
        return getNavTree(this.getRawData());
    }

    getImplsWithValidationAdmin() {
        return getImplsWithValidationAdmin(this.getRawData());
    }

    isPrefManForAnything() {
        return isPrefManForAnything(this.getRawData());
    }

    canManageLookups(implKey) {
        return canManageLookups(this.getRawData(), implKey);
    }

    canAISuggestion(implKey) {
        return canAISuggestion(this.getRawData(), implKey);
    }

    isSuperuser() {
        return this.getRawData().superuser;
    }

    isAggregate() {
        return this.getRawData().aggregate;
    }

    getDescendantOrgKeys() {
        return getDescendantOrgKeys(this.getRawData());
    }

    getImplementationDescendantOrgKeys(...implKeys) {
        return getImplementationDescendantOrgKeys(this.getRawData(), implKeys);
    }

    getExporterFilterOrgKeys(exporterKey) {
        return getExporterFilterOrgKeys(this.getRawData(), exporterKey);
    }

    isGenericXmlExportAvailable(org = null) {
        return isGenericXmlExportAvailable(this.getRawData(), org);
    }

    getImplsWithHiddenFieldAdmin() {
        return getImplsWithHiddenFieldAdmin(this.getRawData());
    }

    getLicensePreference(pref, impl, org = null) {
        return getLicensePreference(this.getRawData(), pref, impl, org);
    }

    getIllegalLabel(entityOrImplKey) {
        const impl = entityOrImplKey.indexOf("/") < 0
            ? entityOrImplKey
            : new EntityName(entityOrImplKey).implKey;
        const label = getLicensePreference(this.getRawData(), "validation-label-illegal", impl);
        return label || "Illegal";
    }

    getLicensePreferenceMutability(pref, impl, org = null) {
        return getLicensePreferenceMutability(this.getRawData(), pref, impl, org);
    }

    getOrganizationalPreference(pref, org = null) {
        return getOrganizationalPreference(this.getRawData(), pref, org);
    }

    hasLicenseFeature(feature, impl) {
        return hasLicenseFeature(this.getRawData(), feature, impl);
    }

    getDefaultSearchConfig() {
        const state = this.getState();
        if (!state.hasValue()) {
            return {enabled: false};
        }
        const raw = state.getValueEnforcing();
        const enabled = !!getOrganizationalPreference(raw, "use-default-search");
        let query;
        if (enabled) {
            try {
                query = getOrganizationalPreference(raw, "default-search-query");
                query = JSON.parse(query);
                if (!query.key || !query.fields) {
                    query = undefined;
                }
            } catch (e) {
                console.warn("Error parsing default search query", e, query);
                query = undefined;
            }
        }
        return {
            enabled,
            query,
        };
    }

    canUseDE() {
        return canUseDE(this.getRawData());
    }

    canUseDqr() {
        return canUseDqr(this.getRawData());
    }

    canUseHex() {
        return canUseHex(this.getRawData());
    }

    canMergeMagicRoots() {
        return canMergeMagicRoots(this.getRawData());
    }

    canMergeLookups() {
        return canMergeLookups(this.getRawData());
    }

    canWrite(implKey) {
        return canWrite(this.getRawData(), implKey);
    }

    canDelete(implKey) {
        return canDelete(this.getRawData(), implKey);
    }

    canCreate(implKey) {
        return canCreate(this.getRawData(), implKey);
    }

    canPrint(implKey) {
        return canPrint(this.getRawData(), implKey);
    }

    getHexForms() {
        return getHexForms(this.getRawData());
    }

}

const navItemShape = {
    label: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
};
navItemShape.items = PropTypes.arrayOf(PropTypes.exact(navItemShape));

UserStore.stateTypes = loadObjectOf(PropTypes.exact({
    username: PropTypes.string.isRequired,
    firstname: PropTypes.string.isRequired,
    lastname: PropTypes.string.isRequired,
    superuser: PropTypes.bool.isRequired,
    mode: PropTypes.string.isRequired,
    navTree: PropTypes.exact({
        items: navItemShape.items,
    }),
    activeOrg: PropTypes.string.isRequired,
    aggregate: PropTypes.bool,
    orgs: sliceTypeBuilder(PropTypes.exact({
        key: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        completeAccess: PropTypes.bool.isRequired,
        lics: sliceTypeBuilder(PropTypes.exact({
            name: PropTypes.string.isRequired,
            key: PropTypes.string.isRequired,
            exporters: PropTypes.array,
            igrants: PropTypes.number.isRequired,
            prefs: PropTypes.objectOf(PropTypes.any),
            prefLocks: PropTypes.objectOf(PropTypes.string),
            features: PropTypes.instanceOf(Set),
            descendants: PropTypes.arrayOf(PropTypes.string),
        })).isRequired,
        prefs: PropTypes.objectOf(PropTypes.any),
    })).isRequired,
}));

const userStore = typeCheckingStore(new UserStore(Dispatcher));

export default userStore;



// WEBPACK FOOTER //
// ./app/data/userStore.js